import React, { useState, useEffect } from "react";
import { Text } from "sprint-common-web";

import { GetSingleTvSeriesResponse } from "../../../../../common/api/catalogue-api/tv-api/GetSingleTvSeriesResponse";
import {
    TvSeasonResponse,
    EpisodeResponse,
    EpisodeVersionResponse
} from "../../../../../common/api/catalogue-api/tv-api/TvSeriesResponse";
import { useCatalogueTvApi } from "../../../../../common/api/catalogue-api/tv-api/useCatalogueTvApi";
import { ContentReferenceType } from "../../../../../common/types/ContentReferenceType";
import { ContentSubtype } from "../../../../../common/types/ContentSubtype";
import { useFileRecordDetailsState } from "../../../../file-records/details/hooks/useFileRecordDetailsState";
import { BuzzVersionsList } from "../../../buzz-version/BuzzVersionsList";
import { MediaBuzzVersion, mapToMediaBuzzVersion } from "../../../buzz-version/MediaBuzzVersion";
import { ContentAssociationTvSeriesDetails } from "../../../content/types/ContentAssociationDetails";
import { checkIsInactiveContentSelected } from "../../../content/utils/checkIsInactiveContentSelected";

interface FileRecordTvSeriesVersionInfoProps {
    tvSeriesId: string;
    seasonId?: string;
    episodeId?: string;
    versionIds: string[];
}

export const FileRecordTvSeriesVersionInfo = (props: FileRecordTvSeriesVersionInfoProps): JSX.Element => {
    const { tvSeriesId, seasonId, episodeId, versionIds } = props;

    const { getSingleTvSeries } = useCatalogueTvApi();

    const { setIsArchivedContentAssigned } = useFileRecordDetailsState();

    const [isLoading, setIsLoading] = useState<boolean>(true);

    const [episodeBuzzVersions, setEpisodeBuzzVersions] = useState<MediaBuzzVersion[]>([]);

    useEffect(() => {
        setIsLoading(true);

        getSingleTvSeries(tvSeriesId)
            .then((response: GetSingleTvSeriesResponse) => {
                const episodeVersions = response.seasons
                    .find((season: TvSeasonResponse) => season.id === seasonId)
                    ?.episodes.find((episode: EpisodeResponse) => episode.id === episodeId)
                    ?.versions.filter((version: EpisodeVersionResponse) => versionIds.includes(version.id));

                const versions = episodeVersions?.map(mapToMediaBuzzVersion) || [];

                const isArchivedContentAssigned = checkIsInactiveContentSelected(
                    mapToContentAssociationDetails(response),
                    {
                        versionIds
                    }
                );

                setIsArchivedContentAssigned(isArchivedContentAssigned);

                setEpisodeBuzzVersions(versions);
            })
            .finally(() => setIsLoading(false));
    }, [tvSeriesId, versionIds]);

    if (!versionIds.length) {
        return <Text value="-" />;
    }

    return <BuzzVersionsList versions={episodeBuzzVersions} isLoading={isLoading} />;
};

const mapToContentAssociationDetails = (response: GetSingleTvSeriesResponse): ContentAssociationTvSeriesDetails => {
    return {
        id: response.id,
        title: response.title,
        type: ContentReferenceType.TvSeries,
        source: response.source,
        orphaned: response.orphaned,
        seasons: response.seasons,
        subtype: ContentSubtype.TV,
        mergedTargetId: response.mergedTargetId
    };
};
